import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import { Link } from 'react-router';
import { api } from '../../lib/api.ts';
import LiveNowBanner from '../../components/LiveNowBanner.tsx';
import {
  Button,
  Card,
  EmptyState,
  ErrorText,
  Field,
  Input,
  Modal,
  Select,
  Spinner,
  Tag,
  Textarea,
  cls,
  fmtDateTime,
  fmtMoney,
} from '../../lib/ui.tsx';

interface Lesson {
  id: string;
  startsAt: string;
  endsAt: string;
  status: string;
  studentName?: string;
  isTrial: boolean;
  privateNotes?: string | null;
  sharedSummary: string | null;
  recurringSlotId: string | null;
}

interface StudentRow {
  userId: string;
  user: { id: string; name: string };
}

interface AvailabilityRow {
  dayOfWeek: number;
  startMin: number;
  endMin: number;
}

interface RecurringSlot {
  id: string;
  studentName?: string;
  dayOfWeek: number;
  startMin: number;
  durationMin: number;
}

interface WaitlistEntry {
  id: string;
  studentName?: string;
  status: string;
  createdAt: string;
}

const DAY_NAMES = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

function startOfWeek(d: Date): Date {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() + 6) % 7)); // back to Monday
  return x;
}

function addDays(d: Date, n: number): Date {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
}

function minToHHMM(min: number): string {
  return `${String(Math.floor(min / 60)).padStart(2, '0')}:${String(min % 60).padStart(2, '0')}`;
}

function hhmmToMin(s: string): number {
  const [h = 0, m = 0] = s.split(':').map(Number);
  return h * 60 + m;
}

function fmtTime(d: string): string {
  return new Date(d).toLocaleTimeString(undefined, { hour: '2-digit', minute: '2-digit' });
}

const chipStyle: Record<string, string> = {
  scheduled: 'bg-brand-100 text-brand-800 hover:bg-brand-200',
  completed: 'bg-emerald-100 text-emerald-800',
  cancelled: 'bg-slate-100 text-slate-400 line-through',
  late_cancelled: 'bg-red-50 text-red-400 line-through',
};

/** G8 · Lesson detail: notes, shared summary, completion, cancellation (G3). */
function LessonModal({ lesson, onClose }: { lesson: Lesson; onClose: () => void }) {
  const qc = useQueryClient();
  const [privateNotes, setPrivateNotes] = useState(lesson.privateNotes ?? '');
  const [sharedSummary, setSharedSummary] = useState(lesson.sharedSummary ?? '');
  const [cancelResult, setCancelResult] = useState<boolean>(false);

  const invalidate = () => void qc.invalidateQueries({ queryKey: ['coach-lessons'] });

  const saveNotes = useMutation({
    mutationFn: (body: Record<string, unknown>) =>
      api(`/api/scheduling/lessons/${lesson.id}/notes`, { method: 'PATCH', body }),
    onSuccess: invalidate,
  });

  const cancel = useMutation({
    mutationFn: () => api(`/api/scheduling/lessons/${lesson.id}/cancel`, { method: 'POST' }),
    onSuccess: (res) => {
      setCancelResult(true);
      invalidate();
    },
  });

  return (
    <Modal open onClose={onClose} title={`Lesson — ${lesson.studentName ?? 'student'}`}>
      <div className="space-y-4">
        <div className="space-y-1 text-sm">
          <p>
            {fmtDateTime(lesson.startsAt)} – {fmtTime(lesson.endsAt)}{' '}
            {lesson.isTrial && <Tag tone="amber">Trial</Tag>}{' '}
            {lesson.status !== 'scheduled' && <Tag tone={lesson.status === 'completed' ? 'green' : 'red'}>{lesson.status}</Tag>}
            {lesson.recurringSlotId && <Tag>recurring</Tag>}
          </p>
          <div className="flex flex-wrap items-center gap-3">
            {lesson.status === 'scheduled' && (
              <Link to={`/coach/live/${lesson.id}`} className="text-brand-700 underline">
                🎥 Open live room
              </Link>
            )}
          </div>
        </div>

        <Field label="Private notes (only you see these)">
          <Textarea rows={3} value={privateNotes} onChange={(e) => setPrivateNotes(e.target.value)} />
        </Field>
        <Field label="What we worked on (shared with student)">
          <Textarea rows={3} value={sharedSummary} onChange={(e) => setSharedSummary(e.target.value)} />
        </Field>

        <div className="flex flex-wrap gap-2">
          <Button onClick={() => saveNotes.mutate({ privateNotes, sharedSummary })} disabled={saveNotes.isPending}>
            Save notes
          </Button>
          {lesson.status === 'scheduled' && (
            <>
              <Button
                variant="secondary"
                onClick={() => saveNotes.mutate({ status: 'completed' })}
                disabled={saveNotes.isPending}
              >
                Mark completed
              </Button>
              <Button variant="danger" onClick={() => cancel.mutate()} disabled={cancel.isPending}>
                Cancel lesson
              </Button>
            </>
          )}
        </div>
        {saveNotes.isSuccess && <p className="text-xs text-emerald-700">Saved ✅</p>}
        {cancelResult && (
          <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800">Lesson cancelled.</p>
        )}
        <ErrorText error={saveNotes.error ?? cancel.error} />
      </div>
    </Modal>
  );
}

function NewLessonModal({ open, onClose }: { open: boolean; onClose: () => void }) {
  const qc = useQueryClient();
  const [studentId, setStudentId] = useState('');
  const [startsAt, setStartsAt] = useState('');
  const [durationMin, setDurationMin] = useState(45);

  const { data: students } = useQuery({
    queryKey: ['students'],
    queryFn: () => api<StudentRow[]>('/api/students'),
    enabled: open,
  });

  const create = useMutation({
    mutationFn: () =>
      api('/api/scheduling/lessons', {
        body: { studentId, startsAt: new Date(startsAt).toISOString(), durationMin },
      }),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['coach-lessons'] });
      onClose();
    },
  });

  return (
    <Modal open={open} onClose={onClose} title="New lesson">
      <div className="space-y-3">
        <Field label="Student">
          <Select value={studentId} onChange={(e) => setStudentId(e.target.value)}>
            <option value="">Choose a student…</option>
            {(students ?? []).map((s) => (
              <option key={s.userId} value={s.userId}>
                {s.user.name}
              </option>
            ))}
          </Select>
        </Field>
        <Field label="Starts at">
          <Input type="datetime-local" value={startsAt} onChange={(e) => setStartsAt(e.target.value)} />
        </Field>
        <Field label="Duration">
          <Select value={durationMin} onChange={(e) => setDurationMin(Number(e.target.value))}>
            <option value={30}>30 min</option>
            <option value={45}>45 min</option>
            <option value={60}>60 min</option>
          </Select>
        </Field>
        <Button onClick={() => create.mutate()} disabled={create.isPending || !studentId || !startsAt}>
          Book lesson
        </Button>
        <ErrorText error={create.error} />
      </div>
    </Modal>
  );
}

/** G1 · Weekly availability windows editor. */
function AvailabilityCard() {
  const qc = useQueryClient();
  const { data } = useQuery({
    queryKey: ['availability'],
    queryFn: () => api<AvailabilityRow[]>('/api/scheduling/availability'),
  });
  const [rows, setRows] = useState<{ dayOfWeek: number; start: string; end: string }[] | null>(null);

  useEffect(() => {
    if (data && rows === null) {
      setRows(data.map((r) => ({ dayOfWeek: r.dayOfWeek, start: minToHHMM(r.startMin), end: minToHHMM(r.endMin) })));
    }
  }, [data, rows]);

  const save = useMutation({
    mutationFn: () =>
      api('/api/scheduling/availability', {
        method: 'PUT',
        body: (rows ?? []).map((r) => ({ dayOfWeek: r.dayOfWeek, startMin: hhmmToMin(r.start), endMin: hhmmToMin(r.end) })),
      }),
    onSuccess: () => {
      setRows(null);
      void qc.invalidateQueries({ queryKey: ['availability'] });
    },
  });

  const update = (i: number, patch: Partial<{ dayOfWeek: number; start: string; end: string }>) =>
    setRows((prev) => (prev ?? []).map((r, j) => (j === i ? { ...r, ...patch } : r)));

  return (
    <Card
      title="Availability"
      actions={
        <Button variant="ghost" onClick={() => setRows((prev) => [...(prev ?? []), { dayOfWeek: 1, start: '09:00', end: '17:00' }])}>
          + Add window
        </Button>
      }
    >
      {rows === null ? (
        <Spinner />
      ) : rows.length === 0 ? (
        <p className="text-xs text-slate-500">No availability yet — add windows so students can book (G2).</p>
      ) : (
        <div className="space-y-2">
          {rows.map((r, i) => (
            <div key={i} className="flex items-center gap-2">
              <Select className="w-28" value={r.dayOfWeek} onChange={(e) => update(i, { dayOfWeek: Number(e.target.value) })}>
                {DAY_NAMES.map((d, di) => (
                  <option key={di} value={di}>
                    {d}
                  </option>
                ))}
              </Select>
              <Input type="time" className="w-28" value={r.start} onChange={(e) => update(i, { start: e.target.value })} />
              <span className="text-xs text-slate-400">–</span>
              <Input type="time" className="w-28" value={r.end} onChange={(e) => update(i, { end: e.target.value })} />
              <button
                className="text-sm text-red-500 hover:text-red-700"
                onClick={() => setRows((prev) => (prev ?? []).filter((_, j) => j !== i))}
                aria-label="Remove window"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
      <div className="mt-3">
        <Button onClick={() => save.mutate()} disabled={save.isPending || rows === null}>
          Save availability
        </Button>
      </div>
      <ErrorText error={save.error} />
    </Card>
  );
}

/** G6 · Weekly recurring lesson slots. */
function RecurringCard() {
  const qc = useQueryClient();
  const [addOpen, setAddOpen] = useState(false);
  const [studentId, setStudentId] = useState('');
  const [dayOfWeek, setDayOfWeek] = useState(1);
  const [time, setTime] = useState('15:00');
  const [durationMin, setDurationMin] = useState(45);

  const { data: slots, isLoading } = useQuery({
    queryKey: ['recurring'],
    queryFn: () => api<RecurringSlot[]>('/api/scheduling/recurring'),
  });
  const { data: students } = useQuery({
    queryKey: ['students'],
    queryFn: () => api<StudentRow[]>('/api/students'),
    enabled: addOpen,
  });

  const invalidate = () => {
    void qc.invalidateQueries({ queryKey: ['recurring'] });
    void qc.invalidateQueries({ queryKey: ['coach-lessons'] });
  };

  const create = useMutation({
    mutationFn: () =>
      api('/api/scheduling/recurring', { body: { studentId, dayOfWeek, startMin: hhmmToMin(time), durationMin } }),
    onSuccess: () => {
      setAddOpen(false);
      invalidate();
    },
  });
  const skip = useMutation({
    mutationFn: ({ id, date }: { id: string; date: string }) =>
      api(`/api/scheduling/recurring/${id}/skip`, { body: { date } }),
    onSuccess: invalidate,
  });
  const remove = useMutation({
    mutationFn: (id: string) => api(`/api/scheduling/recurring/${id}`, { method: 'DELETE' }),
    onSuccess: invalidate,
  });

  const onSkip = (id: string) => {
    const date = window.prompt('Skip which date? (YYYY-MM-DD)');
    if (!date) return;
    if (!/^\d{4}-\d{2}-\d{2}$/.test(date)) {
      window.alert('Please enter a date as YYYY-MM-DD');
      return;
    }
    skip.mutate({ id, date });
  };

  return (
    <Card title="Recurring slots" actions={<Button variant="ghost" onClick={() => setAddOpen(true)}>+ Add</Button>}>
      {isLoading ? (
        <Spinner />
      ) : !slots || slots.length === 0 ? (
        <p className="text-xs text-slate-500">No recurring slots — add one to auto-book the same time every week.</p>
      ) : (
        <div className="space-y-2">
          {slots.map((s) => (
            <div key={s.id} className="flex items-center justify-between gap-2 rounded-lg border border-slate-200 px-3 py-2">
              <p className="text-sm">
                <span className="font-medium">{s.studentName ?? 'Student'}</span> · {DAY_NAMES[s.dayOfWeek]}{' '}
                {minToHHMM(s.startMin)} · {s.durationMin} min
              </p>
              <div className="flex shrink-0 gap-2">
                <Button variant="secondary" className="px-2 py-1 text-xs" onClick={() => onSkip(s.id)}>
                  Skip a date
                </Button>
                <Button
                  variant="danger"
                  className="px-2 py-1 text-xs"
                  onClick={() => window.confirm('Delete this recurring slot? Future lessons will be cancelled.') && remove.mutate(s.id)}
                >
                  Delete
                </Button>
              </div>
            </div>
          ))}
        </div>
      )}
      <ErrorText error={skip.error ?? remove.error} />

      <Modal open={addOpen} onClose={() => setAddOpen(false)} title="New recurring slot">
        <div className="space-y-3">
          <Field label="Student">
            <Select value={studentId} onChange={(e) => setStudentId(e.target.value)}>
              <option value="">Choose a student…</option>
              {(students ?? []).map((s) => (
                <option key={s.userId} value={s.userId}>
                  {s.user.name}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Day of week">
            <Select value={dayOfWeek} onChange={(e) => setDayOfWeek(Number(e.target.value))}>
              {DAY_NAMES.map((d, di) => (
                <option key={di} value={di}>
                  {d}
                </option>
              ))}
            </Select>
          </Field>
          <Field label="Time">
            <Input type="time" value={time} onChange={(e) => setTime(e.target.value)} />
          </Field>
          <Field label="Duration">
            <Select value={durationMin} onChange={(e) => setDurationMin(Number(e.target.value))}>
              <option value={30}>30 min</option>
              <option value={45}>45 min</option>
              <option value={60}>60 min</option>
            </Select>
          </Field>
          <Button onClick={() => create.mutate()} disabled={create.isPending || !studentId}>
            Create slot
          </Button>
          <ErrorText error={create.error} />
        </div>
      </Modal>
    </Card>
  );
}


/** G7 · Who's waiting for a slot. */
function WaitlistCard() {
  const { data, isLoading } = useQuery({
    queryKey: ['coach-waitlist'],
    queryFn: () => api<WaitlistEntry[]>('/api/scheduling/waitlist'),
  });
  return (
    <Card title="Waitlist">
      {isLoading ? (
        <Spinner />
      ) : !data || data.length === 0 ? (
        <p className="text-xs text-slate-500">Nobody is waiting right now.</p>
      ) : (
        <div className="space-y-2">
          {data.map((e) => (
            <div key={e.id} className="flex items-center justify-between text-sm">
              <span>{e.studentName ?? 'Student'}</span>
              <Tag tone={e.status === 'offered' ? 'amber' : 'default'}>{e.status}</Tag>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

/** G4 · ICS subscription URL. */
function IcsCard() {
  const [url, setUrl] = useState<string | null>(null);
  const [copied, setCopied] = useState(false);
  const fetchUrl = useMutation({
    mutationFn: () => api<{ url: string }>('/api/scheduling/ics-url'),
    onSuccess: (res) => setUrl(res.url),
  });
  return (
    <Card title="Calendar feed (ICS)">
      {url ? (
        <div className="space-y-2">
          <Input readOnly value={url} onFocus={(e) => e.target.select()} />
          <Button
            variant="secondary"
            onClick={() => {
              void navigator.clipboard.writeText(url).then(() => setCopied(true));
            }}
          >
            {copied ? 'Copied ✅' : 'Copy URL'}
          </Button>
          <p className="text-xs text-slate-500">Subscribe from Google/Apple Calendar to see all lessons.</p>
        </div>
      ) : (
        <Button variant="secondary" onClick={() => fetchUrl.mutate()} disabled={fetchUrl.isPending}>
          Get calendar URL
        </Button>
      )}
      <ErrorText error={fetchUrl.error} />
    </Card>
  );
}

export default function CalendarPage() {
  const [weekStart, setWeekStart] = useState(() => startOfWeek(new Date()));
  const [selected, setSelected] = useState<Lesson | null>(null);
  const [newOpen, setNewOpen] = useState(false);

  const weekEnd = addDays(weekStart, 7);
  const { data: lessons, isLoading } = useQuery({
    queryKey: ['coach-lessons', weekStart.toISOString()],
    queryFn: () =>
      api<Lesson[]>(`/api/scheduling/lessons?from=${encodeURIComponent(weekStart.toISOString())}&to=${encodeURIComponent(weekEnd.toISOString())}`),
  });

  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <h1 className="text-lg font-bold">Calendar</h1>
        <div className="flex items-center gap-2">
          <Button variant="secondary" onClick={() => setWeekStart((w) => addDays(w, -7))} aria-label="Previous week">
            ←
          </Button>
          <span className="text-sm font-medium text-slate-700">
            {weekStart.toLocaleDateString(undefined, { day: 'numeric', month: 'short' })} –{' '}
            {addDays(weekStart, 6).toLocaleDateString(undefined, { day: 'numeric', month: 'short' })}
          </span>
          <Button variant="secondary" onClick={() => setWeekStart((w) => addDays(w, 7))} aria-label="Next week">
            →
          </Button>
          <Button onClick={() => setNewOpen(true)}>+ New lesson</Button>
        </div>
      </div>

      {/* live room occupied (even outside the scheduled window) */}
      <LiveNowBanner role="coach" />

      {isLoading ? (
        <Spinner />
      ) : (
        <div className="overflow-x-auto rounded-xl border border-slate-200 bg-white">
          <div className="grid min-w-[700px] grid-cols-7 divide-x divide-slate-100">
            {days.map((day) => {
              const dayLessons = (lessons ?? []).filter(
                (l) => new Date(l.startsAt).toDateString() === day.toDateString(),
              );
              const isToday = day.toDateString() === new Date().toDateString();
              return (
                <div key={day.toISOString()} className={cls('min-h-36 p-2', isToday && 'bg-brand-50/50')}>
                  <p className={cls('mb-2 text-center text-xs font-semibold', isToday ? 'text-brand-700' : 'text-slate-500')}>
                    {DAY_NAMES[day.getDay()]} {day.getDate()}
                  </p>
                  <div className="space-y-1">
                    {dayLessons.map((l) => (
                      <button
                        key={l.id}
                        onClick={() => setSelected(l)}
                        className={cls(
                          'w-full rounded-lg px-1.5 py-1 text-left text-[11px] leading-tight',
                          chipStyle[l.status] ?? 'bg-slate-100 text-slate-600',
                        )}
                      >
                        <span className="font-semibold">{fmtTime(l.startsAt)}</span>
                        <br />
                        {l.studentName ?? 'Student'}
                        {l.isTrial && ' · trial'}
                      </button>
                    ))}
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {!isLoading && (lessons ?? []).length === 0 && (
        <EmptyState title="No lessons this week" hint="Book one with “New lesson” or let students self-book from your availability." />
      )}

      <div className="grid gap-4 lg:grid-cols-2">
        <AvailabilityCard />
        <RecurringCard />
        <div className="space-y-4">
          <WaitlistCard />
          <IcsCard />
        </div>
      </div>

      {selected && <LessonModal key={selected.id} lesson={selected} onClose={() => setSelected(null)} />}
      <NewLessonModal open={newOpen} onClose={() => setNewOpen(false)} />
    </div>
  );
}
